import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CategoryDealResponse, DealResponse } from '../models/deal';
import { CouponResponse } from '../models/coupon';
import { GlobalComponent } from '../global-component';
import { Offer } from '../models/brand';
import { ApiResponse } from '../models/apiResponse';
import { SKIP_API_CACHE } from './cache-http.interceptor';
import { ApiCacheService } from './api-cache.service';

const baseUrl = GlobalComponent.BASE_URL;

@Injectable({
  providedIn: 'root',
})
export class DealApiService {
  constructor(
    private httpClient: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private apiCache: ApiCacheService
  ) { }

  getAllDeals(
    brandId: number,
    isActive: number,
    isExpired: number,
    isTop: number,
    isHome: number,
    isLatest: boolean
  ): Observable<DealResponse> {
    // if (isPlatformBrowser(this.platformId)) {
    return this.httpClient.get<DealResponse>(
      baseUrl +
      '/deals/GetUIAllDeals?storeId=' +
      brandId +
      '&isActive=' +
      isActive +
      '&isExpired=' +
      isExpired +
      '&isTop=' +
      isTop +
      '&isHome=' +
      isHome +
      '&isLatest=' +
      isLatest
    );
    // }
    // return of({
    //   data: [],
    //   success: false,
    //   message: 'Server-side rendering',
    // } as unknown as DealResponse);
  }

  getBannerDeals(isTop: number, isActive: number): Observable<DealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<DealResponse>(
        baseUrl + '/deals/GetBannerDeals?isTop=' + isTop + '&isActive=' + isActive
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as DealResponse);
  }

  getAllCoupons(
    brandId: number,
    isActive: boolean,
    isExpired: number,
    isTop: number,
    isHome: number,
    isLatest: boolean,
    recordsCount: number
  ): Observable<CouponResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<CouponResponse>(
        baseUrl +
        '/coupon/GetUIAllCoupons?storeId=' +
        brandId +
        '&isActive=' +
        isActive +
        '&isExpired=' +
        isExpired +
        '&isTop=' +
        isTop +
        '&isHome=' +
        isHome +
        '&isLatest=' +
        isLatest +
        '&isAll=' +
        recordsCount
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as CouponResponse);
  }

  getDealsByStoreNCategory(
    brandId: number,
    isExpired: boolean,
    isTop: number,
    catId: number
  ): Observable<DealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<DealResponse>(
        baseUrl +
        '/deals/GetDealsByStoreNCategory?storeId=' +
        brandId +
        '&CategoryId=' +
        catId +
        '&isExpired=' +
        isExpired +
        '&isTop=' +
        isTop
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as DealResponse);
  }

  getTopDeals(
    brandId: number,
    isActive: number,
    isExpired: number,
    isTop: number,
    isHome: number
  ): Observable<CategoryDealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<CategoryDealResponse>(
        baseUrl +
        '/deals/GetTopDeals?storeId=' +
        brandId +
        '&isActive=' +
        isActive +
        '&isExpired=' +
        isExpired +
        '&isTop=' +
        isTop +
        '&isHome=' +
        isHome
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as CategoryDealResponse);
  }

  getTopStoreDeals(): Observable<DealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<DealResponse>(
        baseUrl + '/deals/GetTopStoreDeals'
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as DealResponse);
  }

  getLatestDealsByCategory(
    catSeoName: string | null
  ): Observable<DealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<DealResponse>(
        baseUrl + '/deals/GetLatestDealsByCategory?catSeoName=' + catSeoName
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as DealResponse);
  }

  getTopCategoryDeals(): Observable<CategoryDealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<CategoryDealResponse>(
        baseUrl + '/deals/GetTopCategoryDeals'
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as CategoryDealResponse);
  }

  getDealBySeoName(seoName: string | null): Observable<DealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<DealResponse>(
        baseUrl + '/deals/GetDealBySeoName?SeoName=' + seoName
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as DealResponse);
  }

  getDealById(dealId: number): Observable<DealResponse> {
    return this.httpClient.get<DealResponse>(
      baseUrl + '/deals/GetDealById?dealId=' + dealId
    );
  }

  getHeaderDeals(
    isExpired: number,
    isFreeShip: number,
    isClearance: number
  ): Observable<DealResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<DealResponse>(
        baseUrl +
        '/deals/getheaderdeals?isExpired=' +
        isExpired +
        '&isFreeShipping=' +
        isFreeShip +
        '&isClearance=' +
        isClearance
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as DealResponse);
  }

  getCouponsByStoreNCategory(
    brandId: number,
    isExpired: boolean,
    isTop: number,
    catId: number
  ): Observable<CouponResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<CouponResponse>(
        baseUrl +
        '/coupon/GetCouponsByStoreNCategory?storeId=' +
        brandId +
        '&CatId=' +
        catId +
        '&isExpired=' +
        isExpired +
        '&isTop=' +
        isTop
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as CouponResponse);
  }

  getCouponsByStore(
    brandSeoName: string | null,
    isExpired: boolean,
    isTop: number
  ): Observable<CouponResponse> {
    // if (isPlatformBrowser(this.platformId)) {
    return this.httpClient.get<CouponResponse>(
      baseUrl +
      '/coupon/GetCouponsByStore?seoName=' +
      brandSeoName +
      '&isExpired=' +
      isExpired +
      '&isTop=' +
      isTop
    );
    // }
    // return of({
    //   data: [],
    //   success: false,
    //   message: 'Server-side rendering',
    // } as unknown as CouponResponse);
  }

  getLatestCouponsByCategory(catId: string | null): Observable<CouponResponse> {
    // if (isPlatformBrowser(this.platformId)) {
    return this.httpClient.get<CouponResponse>(
      baseUrl + '/coupon/GetLatestCouponsByCategory?CatId=' + catId
    );
    // }
    // return of({
    //   data: [],
    //   success: false,
    //   message: 'Server-side rendering',
    // } as unknown as CouponResponse);
  }

  getCouponBySeoName(seoName: string | null): Observable<CouponResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<CouponResponse>(
        baseUrl + '/coupon/GetCouponBySeoName?SeoName=' + seoName
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as CouponResponse);
  }

  getCouponById(couponId: number): Observable<CouponResponse> {
    return this.httpClient.get<CouponResponse>(
      baseUrl + '/coupon/GetCouponById?CouponId=' + couponId
    );
  }

  getHeaderCoupons(
    isExpired: number,
    isFreeShip: number,
    isClearance: number
  ): Observable<CouponResponse> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<CouponResponse>(
        baseUrl +
        '/coupon/getheadercoupons?isExpired=' +
        isExpired +
        '&isFreeShipping=' +
        isFreeShip +
        '&isClearance=' +
        isClearance
      );
    }
    return of({
      data: [],
      success: false,
      message: 'Server-side rendering',
    } as unknown as CouponResponse);
  }

  addOffer(offer: Offer): Observable<ApiResponse<number>> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.post<ApiResponse<number>>(
        baseUrl + '/coupon/addoffer',
        offer,
        { context: new HttpContext().set(SKIP_API_CACHE, true) }
      ).pipe(tap(() => this.apiCache.clearAll()));
    }
    return of({
      data: 0,
      success: false,
      message: 'Server-side rendering',
    } as unknown as ApiResponse<number>);
  }

  updateCouponClickCount(couponId: number): Observable<ApiResponse<number>> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<ApiResponse<number>>(
        baseUrl + '/Coupon/UpdateCouponClickCount?CouponId=' + couponId,
        { context: new HttpContext().set(SKIP_API_CACHE, true) }
      );
    }
    return of({
      data: 0,
      success: false,
      message: 'Server-side rendering',
    } as unknown as ApiResponse<number>);
  }

  updateDealClickCount(dealId: number): Observable<ApiResponse<number>> {
    if (isPlatformBrowser(this.platformId)) {
      return this.httpClient.get<ApiResponse<number>>(
        baseUrl + '/deals/UpdateDealClickCount?DealId=' + dealId,
        { context: new HttpContext().set(SKIP_API_CACHE, true) }
      );
    }
    return of({
      data: 0,
      success: false,
      message: 'Server-side rendering',
    } as unknown as ApiResponse<number>);
  }
}
